import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Header from '../Header';
import { useNavigate } from 'react-router-dom';

const AgeingReportAllAccounts = () => {
    const [company, setCompany] = useState({
        dateFormat: 'nepali',
        vatEnabled: true,
        fiscalYear: {}
    });
    const [data, setData] = useState({
        report: [],
        receivableTotals: { '0-30': 0, '30-60': 0, '60-90': 0, '90-120': 0, 'over-120': 0, total: 0 },
        payableTotals: { '0-30': 0, '30-60': 0, '60-90': 0, '90-120': 0, 'over-120': 0, total: 0 },
        netTotals: { '0-30': 0, '30-60': 0, '60-90': 0, '90-120': 0, 'over-120': 0, total: 0 },
        company: null,
        currentFiscalYear: null,
        initialFiscalYear: null,
        currentCompanyName: ''
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [currentHighlightedRow, setCurrentHighlightedRow] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'accountName', direction: 'asc' });
    const [showTotals, setShowTotals] = useState(true);
    const [exporting, setExporting] = useState(false);

    const searchInputRef = useRef(null);
    const tableContainerRef = useRef(null);
    const navigate = useNavigate();

    const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        withCredentials: true,
    });

    useEffect(() => {
        fetchAgeingData();
    }, []);

    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, []);

    const fetchAgeingData = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/retailer/ageing-report/all-accounts');
            if (response.data.success) {
                setData(response.data.data);
            } else {
                setError('Failed to load ageing data');
            }
        } catch (err) {
            console.error('Error fetching ageing data:', err);
            setError(err.response?.data?.error || 'Failed to load ageing report');
        } finally {
            setLoading(false);
        }
    };

    // const formatCurrency = (num) => {
    //     const number = typeof num === 'string' ? parseFloat(num.replace(/,/g, '')) : Number(num) || 0;
    //     return number.toLocaleString('en-US', {
    //         minimumFractionDigits: 2,
    //         maximumFractionDigits: 2
    //     });
    // };

    const formatCurrency = (num) => {
        const number = typeof num === 'string' ? parseFloat(num.replace(/,/g, '')) : Number(num) || 0;
        if (company.dateFormat === 'nepali') {
            // Indian grouping, two decimals, English digits
            return number.toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        }
        // English (US) grouping by default
        return number.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const applyFilters = () => {
        let filteredReport = data.report.filter(account => {
            const typeMatches = typeFilter === 'all' ||
                (typeFilter === 'receivable' && account.isReceivable) ||
                (typeFilter === 'payable' && !account.isReceivable);

            const searchMatches = searchQuery === '' ||
                account.accountName.toLowerCase().includes(searchQuery.toLowerCase());

            // Filter out accounts with zero net balance
            const hasNonZeroBalance = Math.abs(account.netBalance) > 0.01;

            return typeMatches && searchMatches && hasNonZeroBalance;
        });

        // Apply sorting
        filteredReport = filteredReport.sort((a, b) => {
            // First, sort by accountName (alphabetical)
            let aAccountName = a.accountName.toLowerCase();
            let bAccountName = b.accountName.toLowerCase();

            if (aAccountName < bAccountName) return -1;
            if (aAccountName > bAccountName) return 1;

            // If account names are equal, then apply the selected sort
            if (sortConfig.key && sortConfig.key !== 'accountName') {
                let aValue, bValue;

                if (sortConfig.key === 'type') {
                    aValue = a.isReceivable ? 'receivable' : 'payable';
                    bValue = b.isReceivable ? 'receivable' : 'payable';
                } else if (sortConfig.key === 'netBalance') {
                    aValue = a.netBalance;
                    bValue = b.netBalance;
                } else {
                    aValue = a.buckets[sortConfig.key];
                    bValue = b.buckets[sortConfig.key];
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
            }

            return 0;
        });


        // Calculate filtered totals
        const filteredReceivableTotals = { '0-30': 0, '30-60': 0, '60-90': 0, '90-120': 0, 'over-120': 0, total: 0 };
        const filteredPayableTotals = { '0-30': 0, '30-60': 0, '60-90': 0, '90-120': 0, 'over-120': 0, total: 0 };
        const filteredNetTotals = { '0-30': 0, '30-60': 0, '60-90': 0, '90-120': 0, 'over-120': 0, total: 0 };

        filteredReport.forEach(account => {
            if (account.isReceivable) {
                Object.keys(account.buckets).forEach(key => {
                    if (key !== 'total') {
                        filteredReceivableTotals[key] += account.buckets[key];
                        filteredNetTotals[key] += account.buckets[key];
                    }
                });
                filteredReceivableTotals.total += account.buckets.total;
                filteredNetTotals.total += account.buckets.total;
            } else {
                Object.keys(account.buckets).forEach(key => {
                    if (key !== 'total') {
                        filteredPayableTotals[key] += Math.abs(account.buckets[key]);
                        filteredNetTotals[key] -= Math.abs(account.buckets[key]);
                    }
                });
                filteredPayableTotals.total += Math.abs(account.buckets.total);
                filteredNetTotals.total -= Math.abs(account.buckets.total);
            }
        });

        return {
            filteredReport,
            filteredReceivableTotals,
            filteredPayableTotals,
            filteredNetTotals
        };
    };

    const highlightRow = (rowElement) => {
        // Remove highlight from all rows
        document.querySelectorAll('.account-row').forEach(row => {
            row.classList.remove('highlight-row');
        });

        // Add highlight to selected row
        if (rowElement) {
            rowElement.classList.add('highlight-row');
            setCurrentHighlightedRow(rowElement);

            // Scroll to the row if needed
            if (tableContainerRef.current) {
                const container = tableContainerRef.current;
                const rowTop = rowElement.offsetTop;
                const containerHeight = container.clientHeight;
                const rowHeight = rowElement.clientHeight;

                if (rowTop < container.scrollTop) {
                    container.scrollTop = rowTop;
                } else if (rowTop + rowHeight > container.scrollTop + containerHeight) {
                    container.scrollTop = rowTop + rowHeight - containerHeight;
                }
            }
        }
    };

    const moveHighlight = (direction) => {
        const rows = document.querySelectorAll('.account-row:not([style*="display: none"])');
        if (rows.length === 0) return;

        let currentIndex = 0;
        if (currentHighlightedRow) {
            currentIndex = Array.from(rows).indexOf(currentHighlightedRow);
        }

        let newIndex;
        if (direction === 'down') {
            newIndex = Math.min(currentIndex + 1, rows.length - 1);
        } else {
            newIndex = Math.max(currentIndex - 1, 0);
        }

        highlightRow(rows[newIndex]);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            moveHighlight('down');
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            moveHighlight('up');
        } else if (e.key === 'Enter' && e.target.id === 'searchInput') {
            // Already handled by real-time filtering
        }
    };

    const exportToCSV = async () => {
        setExporting(true);
        try {
            const { filteredReport, filteredReceivableTotals, filteredPayableTotals, filteredNetTotals } = applyFilters();

            let csvContent = "data:text/csv;charset=utf-8,";

            // Add headers
            csvContent += "Account,Type,0-30 Days,31-60 Days,61-90 Days,91-120 Days,Over 120 Days,Closing\n";

            // Add account rows
            filteredReport.forEach(account => {
                const row = [
                    `"${account.accountName}"`,
                    account.isReceivable ? 'Receivable' : 'Payable',
                    account.buckets['0-30'],
                    account.buckets['30-60'],
                    account.buckets['60-90'],
                    account.buckets['90-120'],
                    account.buckets['over-120'],
                    account.netBalance
                ].join(',');
                csvContent += row + "\n";
            });

            // Add totals
            csvContent += `Total Receivables,,${filteredReceivableTotals['0-30']},${filteredReceivableTotals['30-60']},${filteredReceivableTotals['60-90']},${filteredReceivableTotals['90-120']},${filteredReceivableTotals['over-120']},${filteredReceivableTotals.total}\n`;
            csvContent += `Total Payables,,${filteredPayableTotals['0-30']},${filteredPayableTotals['30-60']},${filteredPayableTotals['60-90']},${filteredPayableTotals['90-120']},${filteredPayableTotals['over-120']},${filteredPayableTotals.total}\n`;
            csvContent += `Net Total,,${filteredNetTotals['0-30']},${filteredNetTotals['30-60']},${filteredNetTotals['60-90']},${filteredNetTotals['90-120']},${filteredNetTotals['over-120']},${filteredNetTotals.total}\n`;

            // Create download link
            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", "ageing_report.csv");
            document.body.appendChild(link);

            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Error exporting to CSV:', err);
            alert('Failed to export data');
        } finally {
            setExporting(false);
        }
    };

    const printReport = () => {
        const { filteredReport, filteredReceivableTotals, filteredPayableTotals, filteredNetTotals } = applyFilters();

        if (filteredReport.length === 0) {
            alert("No data to print");
            return;
        }

        const printWindow = window.open('', '_blank', 'width=1000,height=600,scrollbars=yes,resizable=yes');

        const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Ageing Receivables/Payables Report</title>
            <style>
                body { margin: 1cm; font-family: Arial, sans-serif; }
                table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                th, td { border: 1px solid black; padding: 4px; text-align: right; }
                th { background-color: #f2f2f2; text-align: center; }
                .total-row { background-color: #e6e6e6; font-weight: bold; }
                .account-name { text-align: left; }
                .receivable-row { background-color: #e6f7ff; }
                .payable-row { background-color: #fff7e6; }
                .net-total { background-color: #f0f0f0; font-weight: bold; }
                .type-col { text-align: center; width: 100px; }
                .print-header { text-align: center; margin-bottom: 20px; }
                @page { size: landscape; }
            </style>
        </head>
        <body>
            <div class="print-header">
            <h3>${data.currentCompanyName || 'Company Name'}</h3>
                <p>
                ${data.currentCompany?.address || ''}-${data.currentCompany?.ward || ''}, ${data.currentCompany?.city || ''}
            </p>
            <h2>Ageing Report</h2>
            <hr>
        </div>
            <table>
                <thead>
                    <tr>
                        <th>#</th>
                        <th class="account-name">Account</th>
                        <th class="type-col">Type</th>
                        <th>0-30 Days</th>
                        <th>31-60 Days</th>
                        <th>61-90 Days</th>
                        <th>91-120 Days</th>
                        <th>Over 120 Days</th>
                        <th>Closing</th>
                    </tr>
                </thead>
                <tbody>
                    ${filteredReport.map((account, index) => `
                        <tr class="${account.isReceivable ? 'receivable-row' : 'payable-row'}">
                            <td>${index + 1}.</td>
                            <td class="account-name">${account.accountName}</td>
                            <td class="type-col">${account.isReceivable ? 'Receivable' : 'Payable'}</td>
                            <td>${formatCurrency(account.buckets['0-30'])}</td>
                            <td>${formatCurrency(account.buckets['30-60'])}</td>
                            <td>${formatCurrency(account.buckets['60-90'])}</td>
                            <td>${formatCurrency(account.buckets['90-120'])}</td>
                            <td>${formatCurrency(account.buckets['over-120'])}</td>
                            <td>${formatCurrency(account.netBalance)}</td>
                        </tr>
                    `).join('')}
                    ${showTotals ? `
                    <tr class="total-row">
                        <td colspan="3">Total Receivables</td>
                        <td>${formatCurrency(filteredReceivableTotals['0-30'])}</td>
                        <td>${formatCurrency(filteredReceivableTotals['30-60'])}</td>
                        <td>${formatCurrency(filteredReceivableTotals['60-90'])}</td>
                        <td>${formatCurrency(filteredReceivableTotals['90-120'])}</td>
                        <td>${formatCurrency(filteredReceivableTotals['over-120'])}</td>
                        <td>${formatCurrency(filteredReceivableTotals.total)}</td>
                    </tr>
                    <tr class="total-row">
                        <td colspan="3">Total Payables</td>
                        <td>${formatCurrency(filteredPayableTotals['0-30'])}</td>
                        <td>${formatCurrency(filteredPayableTotals['30-60'])}</td>
                        <td>${formatCurrency(filteredPayableTotals['60-90'])}</td>
                        <td>${formatCurrency(filteredPayableTotals['90-120'])}</td>
                        <td>${formatCurrency(filteredPayableTotals['over-120'])}</td>
                        <td>${formatCurrency(filteredPayableTotals.total)}</td>
                    </tr>
                    <tr class="net-total">
                        <td colspan="3">Net Total</td>
                        <td>${formatCurrency(filteredNetTotals['0-30'])}</td>
                        <td>${formatCurrency(filteredNetTotals['30-60'])}</td>
                        <td>${formatCurrency(filteredNetTotals['60-90'])}</td>
                        <td>${formatCurrency(filteredNetTotals['90-120'])}</td>
                        <td>${formatCurrency(filteredNetTotals['over-120'])}</td>
                        <td>${formatCurrency(filteredNetTotals.total)}</td>
                    </tr>
                    ` : ''}
                </tbody>
            </table>
            <script>
                window.onload = function() {
                    setTimeout(function() {
                        window.print();
                        if (confirm('Close print window?')) {
                            window.close();
                        }
                    }, 200);
                };
            <\/script>
        </body>
        </html>
    `;

        printWindow.document.open();
        printWindow.document.write(printContent);
        printWindow.document.close();
    };
    if (loading) {
        return (
            <div className="container-fluid bg-light min-vh-100">
                <Header />
                <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading ageing report...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid bg-light min-vh-100">
                <Header />
                <div className="alert alert-danger mx-4 mt-4">
                    <h4 className="alert-heading">Error</h4>
                    <p>{error}</p>
                    <button className="btn btn-primary" onClick={fetchAgeingData}>Try Again</button>
                </div>
            </div>
        );
    }

    const { filteredReport, filteredReceivableTotals, filteredPayableTotals, filteredNetTotals } = applyFilters();

    return (
        <div className="container-fluid bg-light min-vh-100" onKeyDown={handleKeyDown} tabIndex={0}>
            <Header />

            <div className="row justify-content-center mt-4">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                            <h2 className="h4 mb-0">Ageing Report</h2>
                            <div>
                                <button
                                    className="btn btn-sm btn-light me-2"
                                    onClick={() => setShowTotals(!showTotals)}
                                    title={showTotals ? "Hide Totals" : "Show Totals"}
                                >
                                    <i className={`bi ${showTotals ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                </button>
                                <button className="btn btn-sm btn-light me-2" onClick={printReport} title="Print Report">
                                    <i className="bi bi-printer"></i>
                                </button>
                                <button
                                    className="btn btn-sm btn-light"
                                    onClick={exportToCSV}
                                    disabled={exporting}
                                    title="Export to CSV"
                                >
                                    {exporting ? (
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                    ) : (
                                        <i className="bi bi-download"></i>
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="row mb-4">
                                <div className="col-md-6 mb-2 mb-md-0">
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <i className="bi bi-search"></i>
                                        </span>
                                        <input
                                            type="text"
                                            id="searchInput"
                                            ref={searchInputRef}
                                            className="form-control"
                                            placeholder="Search account name..."
                                            autoComplete='off'
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        {searchQuery && (
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => setSearchQuery('')}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="d-flex flex-wrap gap-2">
                                        <select
                                            id="typeFilter"
                                            className="form-select"
                                            value={typeFilter}
                                            onChange={(e) => setTypeFilter(e.target.value)}
                                            style={{ width: 'auto' }}
                                        >
                                            <option value="all">All Types</option>
                                            <option value="receivable">Receivable Only</option>
                                            <option value="payable">Payable Only</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div
                                className="table-container"
                                ref={tableContainerRef}
                                style={{ maxHeight: '60vh', overflowY: 'auto' }}
                            >
                                <table className="table table-striped table-hover">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>#</th>
                                            <th className="account-name">Account</th>
                                            <th className="type-col">Type</th>
                                            <th>0-30 Days</th>
                                            <th>31-60 Days</th>
                                            <th>61-90 Days</th>
                                            <th>91-120 Days</th>
                                            <th>Over 120 Days</th>
                                            <th>Closing</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredReport.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" className="text-center py-4">
                                                    <div className="d-flex flex-column align-items-center">
                                                        <i className="bi bi-search text-muted" style={{ fontSize: '2rem' }}></i>
                                                        <p className="mt-2 mb-0">No accounts match your search criteria</p>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary mt-2"
                                                            onClick={() => {
                                                                setSearchQuery('');
                                                                setTypeFilter('all');
                                                            }}
                                                        >
                                                            Clear Filters
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredReport.map((account, index) => (
                                                <tr
                                                    key={index}
                                                    className={`account-row ${account.isReceivable ? 'receivable-row' : 'payable-row'}`}
                                                    onClick={(e) => highlightRow(e.currentTarget)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td>{index + 1}.</td>
                                                    <td className="account-name fw-bold">{account.accountName}</td>
                                                    <td className="type-col">
                                                        <span className={`badge ${account.isReceivable ? 'bg-info' : 'bg-warning'}`}>
                                                            {account.isReceivable ? 'Receivable' : 'Payable'}
                                                        </span>
                                                    </td>
                                                    <td>{formatCurrency(account.buckets['0-30'])}</td>
                                                    <td>{formatCurrency(account.buckets['30-60'])}</td>
                                                    <td>{formatCurrency(account.buckets['60-90'])}</td>
                                                    <td>{formatCurrency(account.buckets['90-120'])}</td>
                                                    <td>{formatCurrency(account.buckets['over-120'])}</td>
                                                    <td className="fw-bold">{formatCurrency(account.netBalance)}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                    {showTotals && filteredReport.length > 0 && (
                                        <tfoot className="sticky-bottom">
                                            {/* Receivables Total */}
                                            <tr className="total-row">
                                                <td colSpan="3">Total Receivables</td>
                                                <td>{formatCurrency(filteredReceivableTotals['0-30'])}</td>
                                                <td>{formatCurrency(filteredReceivableTotals['30-60'])}</td>
                                                <td>{formatCurrency(filteredReceivableTotals['60-90'])}</td>
                                                <td>{formatCurrency(filteredReceivableTotals['90-120'])}</td>
                                                <td>{formatCurrency(filteredReceivableTotals['over-120'])}</td>
                                                <td>{formatCurrency(filteredReceivableTotals.total)}</td>
                                            </tr>

                                            {/* Payables Total */}
                                            <tr className="total-row">
                                                <td colSpan="3">Total Payables</td>
                                                <td>{formatCurrency(filteredPayableTotals['0-30'])}</td>
                                                <td>{formatCurrency(filteredPayableTotals['30-60'])}</td>
                                                <td>{formatCurrency(filteredPayableTotals['60-90'])}</td>
                                                <td>{formatCurrency(filteredPayableTotals['90-120'])}</td>
                                                <td>{formatCurrency(filteredPayableTotals['over-120'])}</td>
                                                <td>{formatCurrency(filteredPayableTotals.total)}</td>
                                            </tr>

                                            {/* Net Total */}
                                            <tr className="net-total">
                                                <td colSpan="3">Net Total</td>
                                                <td>{formatCurrency(filteredNetTotals['0-30'])}</td>
                                                <td>{formatCurrency(filteredNetTotals['30-60'])}</td>
                                                <td>{formatCurrency(filteredNetTotals['60-90'])}</td>
                                                <td>{formatCurrency(filteredNetTotals['90-120'])}</td>
                                                <td>{formatCurrency(filteredNetTotals['over-120'])}</td>
                                                <td>{formatCurrency(filteredNetTotals.total)}</td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .filter-container {
                    display: flex;
                    gap: 15px;
                    align-items: center;
                    flex-wrap: wrap;
                    margin-bottom: 20px;
                }
                
                .filter-group {
                    display: flex;
                    gap: 10px;
                    align-items: center;
                }
                
                .account-name {
                    text-align: left;
                }
                
                .type-col {
                    text-align: center;
                    width: 100px;
                }
                
                .receivable-row {
                    background-color: rgba(13, 110, 253, 0.05) !important;
                }
                
                .payable-row {
                    background-color: rgba(255, 193, 7, 0.05) !important;
                }
                
                .total-row {
                    background-color: #e6e6e6;
                    font-weight: bold;
                }
                
                .net-total {
                    background-color: #f0f0f0;
                    font-weight: bold;
                }
                
                .highlight-row {
                    background-color: #5897FB !important;
                    color: white;
                }
                
                .table-container thead th {
                    position: sticky;
                    top: 0;
                    z-index: 10;
                }
                
                .table-container tfoot tr {
                    position: sticky;
                    bottom: 0;
                    z-index: 10;
                }
                
                @media (max-width: 768px) {
                    .filter-container {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                    
                    .filter-group {
                        width: 100%;
                    }
                    
                    .card-header h2 {
                        font-size: 1.2rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default AgeingReportAllAccounts;